import {
  useCampaignParameters,
  useCurrentReportDate,
} from '@features/campaigns';
import { CpgReport } from '../api/schema';
import { CpgReportsParams, useGetCpgReportsQuery } from '../api/api';
import { useCurrentCampaign } from './useCampaign';
import { useEffect, useMemo } from 'react';
import { skipToken } from '@reduxjs/toolkit/query';
import { useAppDispatch } from '@utils/hooks';
import { reportSelected } from '@components/Dashboard/dashboardSlice';
import { useCurrentChannel } from '../../../hooks/useCurrentChannel';

type ReportsHook = {
  reports: CpgReport[];
  loading: boolean;
};

export type UseReportsProps = {
  campaignId: number | undefined;
  reportParams: Omit<CpgReportsParams, 'campaignId'>;
};

export const useCurrentReportsProps = (): UseReportsProps => {
  const { campaign } = useCurrentCampaign();
  const campaignParams = useCampaignParameters();
  const { channel } = useCurrentChannel();

  return useMemo(
    () => ({
      campaignId: campaign?.id,
      reportParams: {
        includeUnapproved: campaignParams.include_unapproved ?? false,
        showRestrictedValues: false,
        channel,
      },
    }),
    [campaign, campaignParams, channel]
  );
};

export const useAllReports = (props: UseReportsProps): ReportsHook => {
  const { campaignId, reportParams } = props;
  const { data, isFetching: loading } = useGetCpgReportsQuery(
    campaignId ? { ...reportParams, campaignId } : skipToken
  );
  const selectedReportDate = useCurrentReportDate();
  const dispatch = useAppDispatch();

  const sortedReports = useMemo(
    () =>
      data?.data.reports.toSorted(
        (a, b) => Date.parse(a.reportDate) - Date.parse(b.reportDate)
      ) ?? [],
    [data]
  );

  const latestReport = sortedReports.at(-1);

  useEffect(() => {
    if (
      (!selectedReportDate && latestReport) ||
      (selectedReportDate &&
        latestReport &&
        !sortedReports
          .map(report => report.reportDate)
          .includes(selectedReportDate))
    ) {
      dispatch(reportSelected(latestReport.reportDate));
    }
  }, [latestReport, selectedReportDate, dispatch, sortedReports]);

  return useMemo(
    () => ({ reports: sortedReports, loading }),
    [sortedReports, loading]
  );
};

export const useCurrentReports = (): ReportsHook => {
  const props = useCurrentReportsProps();

  return useReports(props);
};

export const useReports = (props: UseReportsProps): ReportsHook => {
  const allReportsHook = useAllReports(props);
  const selectedReportDate = useCurrentReportDate();

  const filteredReports = useMemo(
    () =>
      selectedReportDate
        ? allReportsHook.reports.filter(
            report =>
              new Date(report.reportDate) <= new Date(selectedReportDate)
          )
        : allReportsHook.reports,
    [allReportsHook, selectedReportDate]
  );

  return useMemo(
    () => ({
      ...allReportsHook,
      reports: filteredReports,
    }),
    [allReportsHook, filteredReports]
  );
};

export const useReport = (props: UseReportsProps) => {
  const { reports, loading } = useReports(props);
  const sortedReports = useMemo(
    () =>
      reports.toSorted(
        (a, b) => Date.parse(b.reportDate) - Date.parse(a.reportDate)
      ),
    [reports]
  );
  const currentReport = sortedReports.at(0);

  return useMemo(
    () => ({
      report: currentReport,
      loading,
    }),
    [currentReport, loading]
  );
};

export const useCurrentReport = () => {
  const props = useCurrentReportsProps();

  return useReport(props);
};
